import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Common } from "@/components/common";
import { Icon } from "@/components/Icons";
import { Table, Td, Th, CustomScrollbar, ModalContent } from "@/components";
import { useAppDispatch, useAppSelector } from "@/sharedStore/hooks";
import { fetchExclusiveListDetail } from "../providers/slice";
import {
  useAddProduct,
  useRemoveProduct,
  useUpdateExclusiveListName,
} from "../services/exclusiveLists";
import { SnackbarUtils, currencyFormatter } from "@/utils";
import { SearchProductsSelectbox } from "./SearchProductsSelectbox";

const tableHeaderClasses = "bg-neutral-20 font-bold";
const tableCellClasses =
  "!whitespace-normal border-b border-neutral-20 px-2 py-1";
const stickyBottomCellClasses = `${tableCellClasses} bg-neutral-5 sticky bottom-0`;
const tableRowClasses = "hover:bg-neutral-5";

const ExclusiveListDetailPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id") || "";

  const detail = useAppSelector((s) => s.exclusiveListsReducer.currentDetail);
  const loading = useAppSelector((s) => s.exclusiveListsReducer.loading);

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");

  // Products staged for adding — not yet saved to the backend
  const [pendingProducts, setPendingProducts] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const addProductMuta = useAddProduct();
  const removeProductMuta = useRemoveProduct();
  const updateNameMuta = useUpdateExclusiveListName();

  const load = () => {
    if (id) dispatch(fetchExclusiveListDetail(id));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (detail) setNameValue(detail.name);
  }, [detail]);

  // IDs already in the list + pending, so the combobox can exclude them
  const savedProductIds = useMemo(
    () => detail?.products?.map((p) => p.productId) ?? [],
    [detail]
  );
  const pendingProductIds = useMemo(
    () => pendingProducts.map((p) => p.id),
    [pendingProducts]
  );
  const allProductIds = useMemo(
    () => [...savedProductIds, ...pendingProductIds],
    [savedProductIds, pendingProductIds]
  );

  const handleStageProduct = (product: any) => {
    // Ignore if already in the list or already staged
    if (allProductIds.includes(product.id)) return;
    setPendingProducts((prev) => [...prev, product]);
  };

  const handleRemovePending = (productId: string) => {
    setPendingProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const handleSave = async () => {
    if (!id || pendingProducts.length === 0) return;
    setIsSaving(true);
    try {
      for (const product of pendingProducts) {
        const res = await addProductMuta.mutateAsync({
          exclusiveListId: id,
          productId: product.id,
        });
        if (!res?.data?.success) {
          SnackbarUtils.error(`Failed to add "${product.name}".`);
          setIsSaving(false);
          return;
        }
      }
      SnackbarUtils.success(
        `Added ${pendingProducts.length} product${
          pendingProducts.length > 1 ? "s" : ""
        } to list.`
      );
      setPendingProducts([]);
      load();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setPendingProducts([]);
  };

  const handleRemoveProduct = (
    exclusiveListProductId: string,
    productName: string
  ) => {
    setConfirmDelete({ id: exclusiveListProductId, name: productName });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const res = await removeProductMuta.mutateAsync(confirmDelete.id);
    if (res?.data?.success) {
      SnackbarUtils.success(`Removed "${confirmDelete.name}" from list.`);
      setConfirmDelete(null);
      load();
    }
  };

  const handleSaveName = async () => {
    if (!id || !nameValue.trim()) return;
    const res = await updateNameMuta.mutateAsync({
      id,
      name: nameValue.trim(),
    });
    if (res?.data?.success) {
      SnackbarUtils.success("List name updated.");
      setEditingName(false);
      load();
    }
  };

  const productCount = (detail?.products?.length ?? 0) + pendingProducts.length;

  return (
    <div className="flex flex-col p-4 bg-white rounded shadow h-full">
      {/* Header */}
      <div className="flex items-center pb-4 mb-4 shadow-underline">
        <button className="mr-2" onClick={() => navigate(-1)} aria-label="Back">
          <Icon.Back iconType="fill" className="fill-neutral-90" />
        </button>

        {editingName ? (
          <div className="flex items-center gap-2">
            <Common.Input
              autoFocus
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveName();
                if (e.key === "Escape") setEditingName(false);
              }}
            />
            <Common.Button
              size="sm"
              color="blue"
              onClick={handleSaveName}
              isLoading={updateNameMuta.isLoading}
            >
              Save
            </Common.Button>
            <Common.Button
              size="sm"
              outline
              onClick={() => setEditingName(false)}
            >
              Cancel
            </Common.Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <h5 className="text-hd5 font-medium">
              {loading ? "..." : detail?.name}
            </h5>
            <button onClick={() => setEditingName(true)} aria-label="Edit name">
              <Icon.Edit iconType="stroke" size="16" />
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex flex-col flex-1 min-h-0">
        <CustomScrollbar>
          <Table className="relative w-full border border-line border-separate border-spacing-0">
            <thead>
              <tr>
                <Th className={`${tableHeaderClasses} w-[5%]`}>#</Th>
                <Th className={`${tableHeaderClasses} w-[15%]`}>SKU</Th>
                <Th className={tableHeaderClasses}>PRODUCT NAME</Th>
                <Th className={tableHeaderClasses}>UNIT</Th>
                <Th className={`${tableHeaderClasses} w-[20%]`}>COMMENT</Th>
                <Th className={`${tableHeaderClasses} text-right`}>PRICE</Th>
                <Th className={tableHeaderClasses}></Th>
              </tr>
            </thead>
            <tbody>
              {/* Loading */}
              {loading && (
                <tr>
                  <Td colSpan={7}>
                    <Icon.Loading size={60} className="mx-auto my-36" />
                  </Td>
                </tr>
              )}

              {/* Saved product rows */}
              {!loading &&
                detail?.products?.map((p, index) => (
                  <tr key={p.id} className={tableRowClasses}>
                    <Td className={tableCellClasses}>{index + 1}</Td>
                    <Td className={`${tableCellClasses} group`}>
                      <p>{p.productSKU}</p>
                    </Td>
                    <Td className={tableCellClasses}>
                      <div className="flex items-center">
                        <img
                          alt=""
                          loading="lazy"
                          className="mr-2 w-8 h-8 object-cover d-inline-block"
                          src={p.productImageUrl}
                        />
                        <div className="flex flex-col">
                          <p>{p.productName}</p>
                          <div className="flex items-center">
                            <Icon.Tag
                              size={12}
                              iconType="fill"
                              className="fill-neutral-40"
                            />
                            <p className="text-neutral-60">
                              {p.productCategoryName}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Td>
                    <Td className={tableCellClasses}>{p.productUnit}</Td>
                    <Td className={tableCellClasses}></Td>
                    <Td className={`${tableCellClasses} text-right`}>
                      {p.isPriceTBC ? "TBC" : currencyFormatter.format(p.price)}
                    </Td>
                    <Td className={tableCellClasses}>
                      <span data-tip="Remove">
                        <Common.Button
                          variant="outline"
                          color="red"
                          size="md"
                          iconType="fill"
                          iconLeft={Icon.Trash}
                          outline
                          className="bg-transparent border-transparent hover:!border-transparent"
                          onClick={() =>
                            handleRemoveProduct(p.id, p.productName)
                          }
                        />
                      </span>
                    </Td>
                  </tr>
                ))}

              {/* Pending (unsaved) product rows */}
              {!loading &&
                pendingProducts.map((p, index) => (
                  <tr key={p.id} className="bg-blue-10">
                    <Td className={tableCellClasses}>
                      {(detail?.products?.length ?? 0) + index + 1}
                    </Td>
                    <Td className={tableCellClasses}>
                      <p className="text-neutral-60">{p.sku}</p>
                    </Td>
                    <Td className={tableCellClasses}>
                      <div className="flex items-center">
                        <img
                          alt=""
                          loading="lazy"
                          className="mr-2 w-8 h-8 object-cover d-inline-block"
                          src={p.productImageUrl}
                        />
                        <div className="flex flex-col">
                          <p>{p.name}</p>
                        </div>
                      </div>
                    </Td>
                    <Td className={tableCellClasses}>
                      <p className="text-neutral-60">{p.unit}</p>
                    </Td>
                    {/* Comment — not stored */}
                    <Td className={tableCellClasses}></Td>
                    {/* Price */}
                    <Td className={`${tableCellClasses} text-right`}>
                      {p.isPriceTBC ? "TBC" : currencyFormatter.format(p.cost)}
                    </Td>
                    <Td className={tableCellClasses}>
                      <span data-tip="Remove">
                        <Common.Button
                          color="red"
                          size="md"
                          iconType="fill"
                          iconLeft={Icon.Trash}
                          outline
                          className="bg-transparent border-transparent hover:!border-transparent"
                          onClick={() => handleRemovePending(p.id)}
                        />
                      </span>
                    </Td>
                  </tr>
                ))}

              {/* Sticky bottom row — add product */}
              {!loading && (
                <tr>
                  <Td className={stickyBottomCellClasses}></Td>
                  <Td className={stickyBottomCellClasses}></Td>
                  <Td className={stickyBottomCellClasses}>
                    <SearchProductsSelectbox
                      productIds={allProductIds}
                      lineCount={productCount}
                      onSelect={handleStageProduct}
                    />
                  </Td>
                  <Td className={stickyBottomCellClasses}></Td>
                  <Td className={stickyBottomCellClasses}></Td>
                  <Td className={stickyBottomCellClasses}></Td>
                  <Td className={stickyBottomCellClasses}></Td>
                </tr>
              )}
            </tbody>
          </Table>
        </CustomScrollbar>

        {/* Save / Cancel bar — only visible when there are pending changes */}
        {pendingProducts.length > 0 && (
          <div className="pt-2 flex items-center">
            <Common.Button
              className="ml-auto"
              color="transparent"
              size="lg"
              outline
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Common.Button>
            <Common.Button
              color="blue"
              size="lg"
              onClick={handleSave}
              isLoading={isSaving}
              disabled={isSaving}
            >
              Save changes
            </Common.Button>
          </div>
        )}
      </div>

      {/* Confirm remove product modal */}
      <ModalContent
        open={!!confirmDelete}
        onOverlayClick={() => setConfirmDelete(null)}
        panelClassWidth="sm:max-w-sm sm:w-full"
      >
        <div className="p-6">
          <h3 className="text-hd5 font-semibold mb-2">Remove product</h3>
          <p className="text-neutral-60 mb-6">
            Are you sure you want to remove &quot;{confirmDelete?.name}&quot;
            from this list?
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Common.Button
              color="transparent"
              size="lg"
              outline
              onClick={() => setConfirmDelete(null)}
              disabled={removeProductMuta.isLoading}
            >
              Cancel
            </Common.Button>
            <Common.Button
              color="red"
              size="lg"
              onClick={handleConfirmDelete}
              isLoading={removeProductMuta.isLoading}
            >
              Remove
            </Common.Button>
          </div>
        </div>
      </ModalContent>
    </div>
  );
};

export default ExclusiveListDetailPage;
